import { useState, useEffect, useCallback, useRef } from 'react';

const DISMISSED_KEY = 'lb-install-dismissed';
const DISMISS_DAYS = 14;
const DISPLAY_DURATION = 10000;
const FADE_DURATION = 400;

function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true
  );
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [fading, setFading] = useState(false);
  const timerRef = useRef(null);

  const startAutoHide = useCallback(() => {
    timerRef.current = setTimeout(() => {
      setFading(true);
      setTimeout(() => {
        setVisible(false);
        setFading(false);
        localStorage.setItem(DISMISSED_KEY, String(Date.now()));
      }, FADE_DURATION);
    }, DISPLAY_DURATION);
  }, []);

  useEffect(() => {
    if (isStandalone()) return;

    const dismissed = localStorage.getItem(DISMISSED_KEY);
    if (dismissed && Date.now() - Number(dismissed) < DISMISS_DAYS * 86400000) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
      startAutoHide();
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      clearTimeout(timerRef.current);
    };
  }, [startAutoHide]);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    clearTimeout(timerRef.current);
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') setVisible(false);
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  const handleDismiss = useCallback(() => {
    clearTimeout(timerRef.current);
    setFading(true);
    setTimeout(() => {
      setVisible(false);
      setFading(false);
      localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    }, FADE_DURATION);
  }, []);

  if (!visible) return null;

  return (
    <div
      style={{
        ...styles.banner,
        opacity: fading ? 0 : 1,
        transform: fading ? 'translateY(12px)' : 'translateY(0)',
        transition: `opacity ${FADE_DURATION}ms ease, transform ${FADE_DURATION}ms ease`,
      }}
    >
      <div style={styles.content}>
        <img src="/logo192.png" alt="" style={styles.icon} />
        <div>
          <div style={styles.title}>Install LexiBridge</div>
          <div style={styles.subtitle}>Quick access from your home screen</div>
        </div>
      </div>
      <div style={styles.actions}>
        <button onClick={handleDismiss} style={styles.dismiss}>
          Not now
        </button>
        <button onClick={handleInstall} style={styles.install}>
          Install
        </button>
      </div>
      <div style={styles.progressTrack}>
        <div
          style={{
            ...styles.progressBar,
            animation: `installPromptCountdown ${DISPLAY_DURATION}ms linear forwards`,
          }}
        />
      </div>
      <style>{`
        @keyframes installPromptCountdown {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
}

const styles = {
  banner: {
    position: 'fixed',
    bottom: 16,
    left: 16,
    right: 16,
    maxWidth: 420,
    margin: '0 auto',
    background: '#0f1219',
    border: '1px solid rgb(var(--accent-rgb) / 0.2)',
    borderRadius: 12,
    padding: '14px 16px 18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    zIndex: 50000,
    boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
    fontFamily: 'Inter, sans-serif',
    overflow: 'hidden',
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flex: 1,
    minWidth: 0,
  },
  icon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    flexShrink: 0,
  },
  title: {
    color: '#f1f5f9',
    fontSize: 14,
    fontWeight: 600,
  },
  subtitle: {
    color: 'rgba(241,245,249,0.45)',
    fontSize: 12,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  dismiss: {
    background: 'none',
    border: 'none',
    color: 'rgba(241,245,249,0.4)',
    fontSize: 12,
    cursor: 'pointer',
    padding: '6px 8px',
  },
  install: {
    background: 'var(--accent)',
    border: 'none',
    borderRadius: 8,
    color: '#06080f',
    fontSize: 13,
    fontWeight: 600,
    padding: '8px 16px',
    cursor: 'pointer',
  },
  progressTrack: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 3,
    background: 'rgb(var(--accent-rgb) / 0.1)',
    borderRadius: '0 0 12px 12px',
    overflow: 'hidden',
  },
  progressBar: {
    height: '100%',
    background: 'rgb(var(--accent-rgb) / 0.4)',
    borderRadius: '0 0 0 12px',
  },
};
